import merge from 'deepmerge';
import {
  FONT_FAMILY_LIST,
  FONT_SIZE_LIST,
  FONT_WEIGHT_LIST,
  FONT_LEVEL_LIST,
  fontColor
} from '../static/font';
import { LINE_LEVEL_LIST, splitLineStyle } from '../static/line';
import { ThemeParser } from '../../../core/theme';
import { combineMerge, overwriteMerge } from '../../../model/util/array';

export const PARADIGM = {
  // eslint-disable-next-line no-magic-numbers
  symbolSize: [24, 64],
  dvScatterLabelFontSize: 13
};

const defaultSeriesOption = {
  dvSymbol: 'circle',
  dvSymbolSize: PARADIGM.symbolSize,
  clip: true,
  label: { show: true },
  labelLayout: {
    dvAutoLayout: true,
    hideOverlap: true
  },
  // TODO:图形类型扩展后，此处的配置需要抽象出来，根据图形类型进行匹配
  dvItemStyle: [
    {
      id: 'group',
      focus: 'self'
    },
    {
      id: 'circle',
      emphasis: {
        style: {
          opacity: 1
        },
        z2: 999
      },
      style: {
        opacity: 0.84,
        fontSize: PARADIGM.dvScatterLabelFontSize,
        textFill: 'rgba(0,0,0,0.84)',
        fontFamily: FONT_FAMILY_LIST.seriesLabel,
        // 默认不开启描边
        lineWidth: 0
      },
      blur: {
        style: {
          fill: '#000000',
          opacity: 0.18,
          text: ''
        }
      },
      // 默认blur下不展示label
      textContent: {
        style: {
          fontSize: PARADIGM.dvScatterLabelFontSize,
          fill: 'rgba(0,0,0,0.84)',
          fontFamily: FONT_FAMILY_LIST.seriesLabel
        },
        blur: {
          style: {
            text: '',
            opacity: 0.4
          }
        }
      }
    }
  ],
  dvNearSearch: {
    enable: true,
    trigger: 'all'
  },
  dvAutoHide: {
    enable: true,
    delay: 2000
  }
};
const defaultXAxisOption = themeMode => ({
  type: 'value',
  silent: true,
  dvNameLocation: 'auto',
  nameGap: 4,
  nameTextStyle: {
    fontSize: FONT_SIZE_LIST.axisLabel,
    fontFamily: FONT_FAMILY_LIST.axisName,
    fontWeight: FONT_WEIGHT_LIST.axisName,
    color: fontColor(FONT_LEVEL_LIST.LEVEL2, themeMode)
  },
  axisLabel: {
    margin: 4,
    show: true,
    dvAlignEdge: true,
    color: fontColor(FONT_LEVEL_LIST.LEVEL2, themeMode),
    fontSize: FONT_SIZE_LIST.axisLabel,
    fontFamily: FONT_FAMILY_LIST.axisLabel,
    fontWeight: FONT_WEIGHT_LIST.axisLabel
  },
  axisLine: {
    show: false
  },
  splitLine: {
    show: true,
    lineStyle: (index: number, label: string) => {
      const numberOfLabel = parseFloat(label);
      return splitLineStyle(LINE_LEVEL_LIST.LEVEL2, themeMode, numberOfLabel === 0);
    }
  },
  axisTick: { show: false }
});

const defaultYAxisOption = themeMode => ({
  type: 'value',
  silent: true,
  dvNameLocation: 'left',
  nameGap: 3,
  nameTextStyle: {
    fontSize: FONT_SIZE_LIST.axisLabel,
    fontFamily: FONT_FAMILY_LIST.axisName,
    fontWeight: FONT_WEIGHT_LIST.axisName,
    color: fontColor(FONT_LEVEL_LIST.LEVEL2, themeMode)
  },
  axisLabel: {
    margin: 4,
    show: true,
    inside: true,
    dvAlignEdge: true,
    color: fontColor(FONT_LEVEL_LIST.LEVEL2, themeMode),
    fontSize: FONT_SIZE_LIST.axisLabel,
    fontFamily: FONT_FAMILY_LIST.axisLabel,
    fontWeight: FONT_WEIGHT_LIST.axisLabel,
    verticalAlign: 'bottom',
    lineHeight: 12
  },
  axisLine: {
    show: false
  },
  splitLine: {
    show: true,
    lineStyle: (index: number, label: string) => {
      const numberOfLabel = parseFloat(label);
      return splitLineStyle(LINE_LEVEL_LIST.LEVEL2, themeMode, numberOfLabel === 0);
    }
  },
  axisTick: { show: false }
});
const defaultAxisPointerOption = {
  show: true,
  snap: false,
  triggerTooltip: false,
  dvHideDelay: 0,
  dvAutoHide: true,
  triggerOn: 'mousemove',
  label: {
    dvEnableXOffset: true,
    padding: [2, 1, 1, 1],
    fontSize: FONT_SIZE_LIST.axisPointerLabel,
    fontFamily: FONT_FAMILY_LIST.axisPointerLabel,
    lineHeight: 12,
    backgroundColor: '#3366FF',
    borderRadius: 2
  },
  lineStyle: {
    color: '#858585',
    type: 'solid'
  }
};

function parseXAxis(option, themeMode) {
  option.xAxis = option.xAxis.map(xAxisItem => merge(defaultXAxisOption(themeMode), xAxisItem));
}

function parseYAxis(option, themeMode) {
  option.yAxis = option.yAxis.map(yAxisItem => merge(defaultYAxisOption(themeMode), yAxisItem));
}

function parseSeries(option) {
  option.series = option.series.map(seriesItem => {
    return merge(defaultSeriesOption, seriesItem, {
      arrayMerge: overwriteMerge,
      customMerge: key => {
        if (key === 'dvItemStyle') {
          return (a, b) => merge(a, b, { arrayMerge: combineMerge('id') });
        }
      }
    });
  });
}

function parseAxisPointer(option) {
  option.axisPointer = merge(defaultAxisPointerOption, option.axisPointer || {});
}

export function dvScatterThemeParse(...args: Parameters<ThemeParser>): ReturnType<ThemeParser> {
  const [option, chart] = args;
  if (typeof option?.series === 'undefined') {
    return;
  }

  const dvScatterSeries = option.series.filter(item => item.type === 'dvScatter');
  if (dvScatterSeries.length === 0) {
    return;
  }
  const themeMode = chart.getModel().getThemeName().includes('light') ? 'light' : 'dark';

  parseXAxis(option, themeMode);
  parseYAxis(option, themeMode);
  parseSeries(option);
  parseAxisPointer(option);
}
